/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { jsx } from '@atlaskit/css';
import { useAppOAuth } from '@atlassian/conversation-assistant-chat-prompt-input/useAppOAuth';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { HumanMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ConnectorState } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { ConnectorRecommendation } from '@atlassian/conversation-assistant-ui-components/ui/ConnectorRecommendation';

interface StoreConnectorRecommendationProps {
	connector: ConnectorState;
	lastHumanMessage: HumanMessage | undefined;
	handleResendMessage: (message: string | DocNode) => void;
}

/**
 * Renders the connector recommendation card and wires up OAuth.
 *
 * This component is only mounted by chat-history once all eligibility
 * conditions have been confirmed (first non-historical assistant message,
 * streaming complete, matching unconnected connector detected, and the
 * experiment gate is enabled). All expensive setup — `useAppOAuth` in
 * particular — is therefore only performed when the card will actually be
 * shown.
 *
 * On successful OAuth the card transitions to a "connected" confirmation state.
 * If the user has not sent a new prompt since the card was shown, a "Re-run
 * prompt" CTA is also displayed so they can opt to refresh the response with
 * the newly connected data.
 */
export const StoreConnectorRecommendation = ({
	connector,
	lastHumanMessage,
	handleResendMessage,
}: StoreConnectorRecommendationProps): React.ReactElement => {
	const { sendAnalyticsEvent } = useAnalytics();

	// Whether the user has successfully completed OAuth for this connector.
	const [isConnected, setIsConnected] = useState(false);

	// Capture the id of the last human message at mount so we can detect
	// whether a new prompt was sent before the user clicks "Connect".
	const humanMessageIdAtRenderRef = useRef(lastHumanMessage?.id);

	// Keep a ref to the latest prop value so the re-run handler sees the
	// current message rather than a stale closure.
	const latestHumanMessageRef = useRef(lastHumanMessage);
	latestHumanMessageRef.current = lastHumanMessage;

	// Tracks whether the user has attempted OAuth at least once (used to
	// distinguish a retry click from a first attempt in analytics).
	const hasAttemptedConnectRef = useRef(false);

	// Analytics guards — each fires its event exactly once.
	const cardViewedLoggedRef = useRef(false);
	const connectedConfirmationLoggedRef = useRef(false);

	// The user can re-run their original prompt only if they haven't sent a new
	// one since the card was shown.
	const canRerunPrompt =
		isConnected &&
		humanMessageIdAtRenderRef.current !== undefined &&
		humanMessageIdAtRenderRef.current === lastHumanMessage?.id;

	const handleOAuthCompleted = useCallback(() => {
		setIsConnected(true);
	}, []);

	const { onAuthorize } = useAppOAuth({
		friendlyName: connector.friendlyName,
		appSource: connector.source,
		outboundAuthUrl: connector.outboundAuthUrl,
		analyticsAttributes: {
			source: 'responseConnectorRecommendation',
		},
		onCompletedAction: handleOAuthCompleted,
	});

	const handleConnect = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'connectButton',
			attributes: {
				source: 'responseConnectorRecommendation',
				appSource: connector.source,
				isRetry: hasAttemptedConnectRef.current,
			},
		});
		hasAttemptedConnectRef.current = true;
		onAuthorize();
	}, [onAuthorize, sendAnalyticsEvent, connector.source]);

	const handleRerunPrompt = useCallback(() => {
		const latest = latestHumanMessageRef.current;
		if (latest === undefined) {
			return;
		}
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rerunPromptButton',
			attributes: {
				source: 'responseConnectorRecommendation',
				appSource: connector.source,
			},
		});
		handleResendMessage(latest.content);
	}, [handleResendMessage, sendAnalyticsEvent, connector.source]);

	// Fire "card shown" once on mount.
	useEffect(() => {
		if (!cardViewedLoggedRef.current) {
			cardViewedLoggedRef.current = true;
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'connectorRecommendation',
				attributes: {
					source: 'responseConnectorRecommendation',
					appSource: connector.source,
				},
			});
		}
	}, [sendAnalyticsEvent, connector.source]);

	// Fire "connected confirmation" once when the user first connects.
	useEffect(() => {
		if (isConnected && !connectedConfirmationLoggedRef.current) {
			connectedConfirmationLoggedRef.current = true;
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'connectorRecommendation',
				actionSubjectId: 'connectedConfirmation',
				attributes: {
					source: 'responseConnectorRecommendation',
					appSource: connector.source,
					withRerunPromptCTA: canRerunPrompt,
				},
			});
		}
	}, [isConnected, canRerunPrompt, sendAnalyticsEvent, connector.source]);

	return (
		<ConnectorRecommendation
			friendlyName={connector.friendlyName}
			appSource={connector.source}
			outboundAuthUrl={connector.outboundAuthUrl}
			iconUrl={connector.iconUrl}
			onConnect={handleConnect}
			isConnected={isConnected}
			canRerunPrompt={canRerunPrompt}
			onRerunPrompt={handleRerunPrompt}
		/>
	);
};
